import React from 'react';
import {
	Box,
	Text,
	HStack,
	VStack,
	Circle,
	Icon,
	Button,
	useColorModeValue,
} from '@chakra-ui/react';

export const StatTile = ({
	title,
	value,
	subtitle,
	icon,
	gradient = 'linear(135deg, teal.400, teal.600)',
	bgGradient = 'linear(teal.400, teal.600)',
	onClick,
}) => {
	const cardBg = useColorModeValue('white', 'gray.800');
	const cardBorder = useColorModeValue('gray.200', 'gray.600');
	const textColor = useColorModeValue('gray.800', 'white');
	const subtitleColor = useColorModeValue('gray.500', 'gray.400');

	return (
		<Box
			bg={cardBg}
			border='1px solid'
			borderColor={cardBorder}
			borderRadius='2xl'
			p={5}
			position='relative'
			overflow='hidden'
			boxShadow='sm'
			cursor={onClick ? 'pointer' : 'default'}
			transition='all 0.2s'
			_hover={onClick ? { shadow: 'md', transform: 'translateY(-2px)' } : undefined}
			onClick={onClick}
			role={onClick ? 'button' : undefined}
			tabIndex={onClick ? 0 : undefined}
			onKeyDown={
				onClick
					? (e) => {
							if (e.key === 'Enter' || e.key === ' ') onClick();
						}
					: undefined
			}>
			<Box
				position='absolute'
				top='-16px'
				right='-16px'
				w='80px'
				h='80px'
				borderRadius='full'
				bgGradient={bgGradient}
				opacity={0.12}
				pointerEvents='none'
			/>
			<HStack spacing={4} align='center'>
				<Circle size='48px' bgGradient={gradient} flexShrink={0}>
					<Icon as={icon} color='white' boxSize={5} />
				</Circle>
				<VStack align='start' spacing={0} minW={0}>
					<Text
						fontSize='xs'
						color={subtitleColor}
						textTransform='uppercase'
						letterSpacing='wide'
						fontWeight='semibold'>
						{title}
					</Text>
					<Text fontSize='2xl' fontWeight='bold' color={textColor} lineHeight='1.1'>
						{value}
					</Text>
					{subtitle && (
						<Text fontSize='xs' color={subtitleColor} mt={1} noOfLines={1}>
							{subtitle}
						</Text>
					)}
				</VStack>
			</HStack>
		</Box>
	);
};

export const SectionCard = ({ title, description, action, children, ...props }) => {
	const cardBg = useColorModeValue('white', 'gray.800');
	const cardBorder = useColorModeValue('gray.200', 'gray.600');

	return (
		<Box
			bg={cardBg}
			border='1px solid'
			borderColor={cardBorder}
			borderRadius='2xl'
			overflow='hidden'
			boxShadow='sm'
			{...props}>
			{(title || action) && (
				<Box px={5} py={4} borderBottom='1px solid' borderColor={cardBorder}>
					<HStack justify='space-between' align='start'>
						<Box>
							{title && (
								<Text fontWeight='bold' fontSize='md' color='gray.800'>
									{title}
								</Text>
							)}
							{description && (
								<Text fontSize='sm' color='gray.500' mt={title ? 1 : 0}>
									{description}
								</Text>
							)}
						</Box>
						{action}
					</HStack>
				</Box>
			)}
			<Box p={5}>{children}</Box>
		</Box>
	);
};

export const EmptyPanel = ({ message, actionLabel, onAction }) => (
	<VStack py={8} spacing={3}>
		<Text fontSize='sm' color='gray.500' textAlign='center'>
			{message}
		</Text>
		{actionLabel && onAction && (
			<Button size='sm' colorScheme='teal' onClick={onAction}>
				{actionLabel}
			</Button>
		)}
	</VStack>
);
